// @flow

import isArray from 'lodash/fp/isArray';
import mapValues from 'lodash/mapValues';
import omit from 'lodash/omit';

import { createSelector } from 'reselect';

import {
  selectCartOwner,
  selectCartProductOwner,
  selectCartReselleeID,
  selectRawProductsArray,
  updateProducts,
} from '@gandi/cart';
import type { Product, Process, ProductType } from '@gandi/cart';
import {
  getBaseContactFields,
  getBaseContactFieldsField,
  type ContactTypeString as OrganizationType,
} from '@gandi/domain-configuration';
import {
  fetchOneOrganization,
  selectOneOrganization,
} from '@gandi/redux-organization';
import { contactbook } from '@gandi/redux-resource-contactbook';
import type { FavoriteContact } from '@gandi/redux-resource-contactbook';
import type { Organization } from '@gandi/redux-sharing';
import { useTranslator } from '@gandi/translate';

import { bulkConfiguration } from 'src/modules/configurations';
import { selectShopCurrent } from 'src/modules/organization/selectors';
import type { Dispatch, GetState, FullState } from 'src/types';

import { resellableCartConfKeys } from '../../AccountChoice/modules/helpers';
import {
  fetchOneCustomer,
  selectOneCustomer,
} from '../../Customer/modules/customers';
import type { CustomerDetail, ListCustomer } from '../../Customer/types';
import {
  fieldsMapping,
  getOwnerContactExtraParams,
} from '../../DomainContacts/Wizard/modules/actions';
import {
  selectCurrentContactByRoles,
  selectHasAnyFavoriteContact,
  selectHasDomainCreate,
  selectHasDomainTransfer,
  selectSetupOwner,
  selectShouldFetchFavoriteContacts,
  selectShouldFetchOneContactExtraParams,
  selectShouldFetchOwnerExtraParams,
} from '../../Setup/modules/selectors';

// INFO: copy of the original BaseContact but extra_parameters is optional
export type BaseContact = {
  [string]: void | boolean | Organization,
  extra_parameters?: { vat_number: any },
};

type ProductFilter = Array<{ productType: ProductType, process: Process }>;

type Options = {
  /** an array to tell which kind of product are patched
   *  if empty, every products are patched */
  filter?: ProductFilter,
  /** when activated, the product owner will be set in cart configuration only
   * else each product contain its ownership data
   * WARN: some front process may not support the ownership from the cart configuration */
  allowToSetOwnershipInCartConf?: boolean,
};

const useGetBaseContact = () => {
  const translator = useTranslator();

  return () => async (dispatch: Dispatch, getState: GetState) => {
    // grab current orga
    const current: Organization = selectShopCurrent(getState(), true);

    // works with cart ownership
    const cartReselleeId: string = selectCartReselleeID(getState());
    const cartProductOwnerId: string = selectCartProductOwner(getState());
    // NOTE: the owner from the cart and the current.id should be the same
    const cartOwnerId = selectCartOwner(getState()) || current.id;
    const isReseller = current.reseller;

    const customer = selectOneCustomer(getState(), cartReselleeId);
    // fetch the customer if needed
    if (cartReselleeId && !customer && isReseller) {
      dispatch(fetchOneCustomer(current.id, cartReselleeId));
    }

    /** cart product owner unless customer */
    const naturalCartProductOwnerId = cartProductOwnerId || cartOwnerId;
    const naturalCartProductOwner =
      // fallback to the cartOwnerId if no specific cart owner is set
      selectOneOrganization(getState(), naturalCartProductOwnerId);
    // fetch naturalCartProductOwner if needed
    if (!naturalCartProductOwner && !cartReselleeId) {
      fetchOneOrganization(naturalCartProductOwnerId);
    }

    /** cart product owner considering customer if any */
    const cartProductOwner:
      | Organization
      | CustomerDetail
      | ListCustomer
      | void =
      isReseller && cartReselleeId
        ? customer
        : // $FlowFixMe[incompatible-type] fields as lang and autorenew may miss in some types from the union
          naturalCartProductOwner || current;

    if (!cartProductOwner) {
      // NOTE: this inherit form the original getBaseContact
      throw new Error('Target Organization Unavailable.');
    }

    // NOTE: this inherit form the original getBaseContact
    // $FlowFixMe[incompatible-type] Fix Organization type of redux-sharing
    const type: OrganizationType = cartProductOwner.type;

    // TODO: modernize this getBaseContactFields without translator needs
    const baseContactFields = getBaseContactFields(translator, type);

    // Initialize values object
    const values: BaseContact = {};

    // Assign default values
    baseContactFields
      .filter(field => 'defaultValue' in field)
      .forEach(field => {
        if (field.field) {
          values[field.field] = field.defaultValue;
        }
      });

    // Mapping selected data from state to field values ...
    // TODO: modernize this getBaseContactFieldsField without translator needs
    const baseContactFieldsField = getBaseContactFieldsField(translator, type);
    baseContactFieldsField.forEach(field => {
      // NOTE: this inherit form the original getBaseContact
      // $FlowFixMe[invalid-computed-prop] flow does not like to access property this way. It should not be an issue at all.
      const mappedField = fieldsMapping[field] ?? field;

      const value = cartProductOwner[mappedField];
      if (value) {
        values[field] = value;
      }
    });

    // NOTE: this inherit form the original getBaseContact
    // FIXME: should be a field mapping
    // Retrieve other extra parameters such as the VAT number
    const vatNumber = cartProductOwner.vat_number;
    if (vatNumber) {
      values.extra_parameters = {
        vat_number: vatNumber,
      };
    }
    // Checking for missing data in the values object...
    // const missing = _.difference(fields, Object.keys(values));
    return values;
  };
};

const selectResellingPayload: FullState => {
  reseller_id: string | void,
  resellee_id: string | void,
  owner_name: string | void,
} = (state: FullState) => {
  const reselleeId = selectCartReselleeID(state);
  const cartOwnerId = selectCartOwner(state);
  const customer = selectOneCustomer(state, reselleeId);
  return {
    resellee_id: reselleeId || undefined,
    reseller_id: reselleeId ? cartOwnerId || undefined : undefined,
    owner_name: customer?.name || undefined,
  };
};

const selectHasCertificateCreate: (state: FullState) => boolean =
  createSelector([selectRawProductsArray], products =>
    products.some(
      product =>
        product.productType === 'certificate' && product.process === 'create',
    ),
  );

const selectContactbookOrga: (state: FullState) => string = createSelector(
  [selectCartReselleeID, selectSetupOwner],
  (reselleeId, setupOwner) => reselleeId || setupOwner,
);

/** required to `selectCurrentContactByRoles` */
const fetchContactbookIfNeeded = (): ((Dispatch, GetState) => Promise<any>) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    const setupOwner = selectSetupOwner(getState());
    if (selectShouldFetchFavoriteContacts(getState(), setupOwner)) {
      await dispatch(contactbook.fetchContactsWithRole(setupOwner));
    }
  };
};

/** required to selectOwnerContactExtraParams */
const fetchOwnerExtraParamsIfNeeded = (): ((
  Dispatch,
  GetState,
) => Promise<any>) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    const setupOwner = selectSetupOwner(getState());
    if (selectShouldFetchOwnerExtraParams(getState(), { orgId: setupOwner })) {
      await dispatch(contactbook.fetchOwnerContactExtraParams(setupOwner));
    }
  };
};

const fetchExtraParams = (): ((Dispatch, GetState) => Promise<any>) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    const contactbookOrgId = selectSetupOwner(getState());

    // Load the extra params for the contacts with role for the contactbookOrgId.
    // We load them for all the TLDs at once, it might be cheaper than to do a call for each TLD.
    const defaultContacts: Array<FavoriteContact> = (Object.values(
      contactbook.selectContactByRole(getState(), contactbookOrgId),
    ): any);

    return await Promise.allSettled(
      defaultContacts
        .filter(contact =>
          selectShouldFetchOneContactExtraParams(getState(), {
            orgId: contactbookOrgId,
            contactId: contact.id,
          }),
        )
        .map(contact =>
          dispatch(
            contactbook.fetchContactExtraParams(contactbookOrgId, contact.id),
          ),
        ),
    );
  };
};

/** return pass products to be updated */
const getPaasAndDomainReadyToPatch = ({
  baseContact,
  setOwnershipInCartConfiguration,
}: {
  baseContact: BaseContact,
  setOwnershipInCartConfiguration: boolean,
}): ((Dispatch, GetState) => Promise<Array<Product>>) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    // use the right contactbook
    const contactbookOrgId = selectContactbookOrga(getState());

    // grab favorites contact for create, because domain into a paas are create
    const contacts = selectCurrentContactByRoles(getState(), 'create');

    const productsToPatch = selectRawProductsArray(getState()).filter(
      p =>
        (p.productType === 'paas' && !!p.payload.domain?.fqdn) ||
        p.productType === 'domain',
    );

    const ownerContactExtraParams = await dispatch(
      getOwnerContactExtraParams(
        contactbookOrgId,
        productsToPatch
          // $FlowFixMe[incompatible-call] update @gandi/cart ProductPayload
          .map(product => product.payload.tld)
          .concat(productsToPatch.map(p => p.payload.domain?.tld || '')),
      ),
    );

    const hasFavoriteContact = selectHasAnyFavoriteContact(getState());

    const resellingPayload = selectResellingPayload(getState());
    // patch domain in paas payload
    return (
      productsToPatch
        // keep domains with sepecific TLD extra parameters or favorite contact
        .filter(
          p =>
            p.productType === 'paas' ||
            (p.productType === 'domain' &&
              (!setOwnershipInCartConfiguration || // if ownership is not set through the cart configuration
                !!ownerContactExtraParams[p.payload.tld] || // or there are specific owner extra parameters
                hasFavoriteContact)), // or there is some contact favorite to apply
        )
        .map(p => {
          const basePayload =
            p.productType === 'domain' ? p.payload : p.payload.domain;
          const cleanPayload = omit(basePayload, [
            'admin',
            'tech',
            'bill',
            'trustee',
            'trustee_roles',
          ]);

          const tld = p.payload.domain?.tld;

          const contactsWithExtraParams = mapValues(
            contacts,
            (contact, role) =>
              contact?.id
                ? {
                    ...contact,
                    extra_parameters:
                      contactbook.selectContactExtraParams(
                        getState(),
                        contactbookOrgId,
                        contact.id,
                        // $FlowFixMe improve @gandi/cart ProductData
                      )?.[tld]?.[role] ?? {},
                  }
                : contact,
          );

          const ownerExtraParameters = {
            ...baseContact.extra_parameters,
            ...(ownerContactExtraParams[p.payload.tld] || {}),
          };

          const newDomainPayload = {
            ...cleanPayload,
            ...resellingPayload,
            ...contactsWithExtraParams,
            owner: {
              ...baseContact,
            },
          };

          // NOTE: important for strong testing
          // add extra_parameters only if needed
          if (Object.keys(ownerExtraParameters).length > 0) {
            newDomainPayload.owner.extra_parameters = ownerExtraParameters;
          }

          return p.productType === 'paas'
            ? { ...p, payload: { ...p.payload, domain: newDomainPayload } }
            : { ...p, payload: newDomainPayload };
        })
    );
  };
};

/** prepare a filter fonction depending on the filter argument */
const getFilterFncFromFilterProductArg = (
  filter: ProductFilter | void,
): ((ProductType, Process) => boolean) =>
  !!filter && isArray(filter)
    ? (productType, process) =>
        filter.some(f => f.productType === productType && f.process === process)
    : () => true;

const patchCartConfiguration = ({
  baseContact,
  options,
}: {
  baseContact: BaseContact,
  options: ?Options,
}): ((Dispatch, GetState) => Promise<any>) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    /** a table to tell which product are present into the cart
     * useful to filter cart configurations */
    const productPresenceTable: {
      [key: ProductType]: { [key: Process]: boolean },
    } = {
      certificate: {
        create: selectHasCertificateCreate(getState()),
      },
      domain: {
        create: selectHasDomainCreate(getState()),
        transfer: selectHasDomainTransfer(getState()),
      },
    };

    // prepare reseller / resellee
    const resellingPayload = selectResellingPayload(getState());

    const filterFncFromFilterProductArg = getFilterFncFromFilterProductArg(
      options?.filter,
    );

    const bulkParams = resellableCartConfKeys
      .filter(
        ({ product_type, process }) =>
          !!productPresenceTable[product_type]?.[process] &&
          filterFncFromFilterProductArg(product_type, process),
      )
      .map(({ product_type, process }) => {
        const productCartConfiguration = {
          product_type,
          process,
          payload: {
            ...resellingPayload,
          },
        };

        if (
          product_type === 'domain' &&
          !!options?.allowToSetOwnershipInCartConf
        ) {
          const contactByRoles =
            process === 'create' || process === 'transfer'
              ? selectCurrentContactByRoles(getState(), process)
              : {};

          // $FlowFixMe[cannot-spread-indexer] contactByRole is a map of role to contact, but flow does not like it
          productCartConfiguration.payload = {
            ...productCartConfiguration.payload,
            ...{ owner: baseContact }, // owner contact
            ...contactByRoles, // favorite contact by role
          };
        }

        return productCartConfiguration;
      });

    return bulkParams.length > 0
      ? dispatch(bulkConfiguration(bulkParams))
      : Promise.resolve();
  };
};

const patchProductPayload = ({
  baseContact,
  options,
}: {
  baseContact: BaseContact,
  options: ?Options,
}): ((Dispatch, GetState) => Promise<any>) => {
  return async (dispatch: Dispatch /* getState: GetState */) => {
    const filterFncFromFilterProductArg = getFilterFncFromFilterProductArg(
      options?.filter,
    );

    const productsToPatch = await dispatch(
      getPaasAndDomainReadyToPatch({
        baseContact,
        setOwnershipInCartConfiguration:
          !!options?.allowToSetOwnershipInCartConf,
      }),
    ).then(res => {
      return res.filter(p =>
        filterFncFromFilterProductArg(p.productType, p.process),
      );
    });

    return productsToPatch.length > 0
      ? await dispatch(
          updateProducts({
            productsData: productsToPatch,
            options: {
              headers: { 'X-Dry-Run': 0 },
            },
          }),
        )
      : Promise.resolve();
  };
};

/**
 * updates the cart configuration for ownership,
 * including product contacts role and reseller status
 */
export const useUpdateCartProductsOwnership = (): ((
  ?Options,
) => (Dispatch, GetState) => Promise<any>) => {
  const getBaseContact = useGetBaseContact();

  return options => {
    return async (dispatch: Dispatch /* getState: GetState */) => {
      // base contact should be grab first
      const baseContact = await dispatch(getBaseContact());
      // prepare extra params and contactbook
      await Promise.allSettled([
        dispatch(fetchContactbookIfNeeded()), // grab the contact of the current product owner
        dispatch(fetchOwnerExtraParamsIfNeeded()), // grab specific extra params for the owner
        dispatch(fetchExtraParams()), // grab specific extra params for other roles
      ]);

      await dispatch(patchProductPayload({ baseContact, options }));
      return dispatch(patchCartConfiguration({ baseContact, options }));
    };
  };
};
